import React, { useState } from 'react';
import { addBudgetOperationHistoryEntry } from '../utils/storage';
import { completeBudgetOperation, uncompleteBudgetOperation } from '../api/index';

const QuickActionModal = ({ operation, currentUser, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    action_type: 'action_added',
    description: '',
    user_name: currentUser?.name || currentUser?.email || 'Usuario',
    new_budget: operation?.subir_ppto_diario_a || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const actionTypes = [
    { value: 'action_added', label: '⚡ Acción Agregada', color: 'bg-purple-100 text-purple-800' },
    { value: 'observation_added', label: '📝 Observación Agregada', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'budget_increased', label: '💰 Presupuesto Modificado', color: 'bg-orange-100 text-orange-800' },
    { value: 'completed', label: '✅ Marcar como Completado', color: 'bg-green-100 text-green-800' },
    { value: 'uncompleted', label: '🔄 Marcar como Pendiente', color: 'bg-red-100 text-red-800' }
  ];

  const quickTemplates = {
    action_added: [
      'Se implementó seguimiento de conversiones',
      'Se optimizaron las palabras clave',
      'Se ajustaron las pujas automáticas',
      'Se crearon nuevos anuncios',
      'Se configuró el remarketing'
    ],
    observation_added: [
      'Cliente solicita aumentar presupuesto',
      'Campaña mostrando buenos resultados',
      'Necesita revisión de audiencias',
      'Requiere optimización de landing page',
      'Pendiente aprobación de creativos'
    ],
    budget_increased: [
      'Presupuesto aumentado por buen rendimiento',
      'Incremento solicitado por el cliente',
      'Ajuste por temporada alta',
      'Aumento por nuevos productos',
      'Optimización de distribución de presupuesto'
    ],
    completed: [
      'Operación completada exitosamente',
      'Todas las tareas han sido finalizadas',
      'Presupuesto implementado correctamente',
      'Campaña configurada y activa',
      'Proceso finalizado según especificaciones'
    ],
    uncompleted: [
      'Operación requiere revisión adicional',
      'Pendiente de aprobación del cliente',
      'Necesita ajustes antes de finalizar',
      'En espera de recursos adicionales',
      'Requiere validación técnica'
    ]
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setError('La descripción es requerida');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (formData.action_type === 'completed') {
        // Marcar como completado usando la función de la API
        await completeBudgetOperation(operation.id, formData.user_name);
      } else if (formData.action_type === 'uncompleted') {
        // Marcar como pendiente usando la función de la API
        await uncompleteBudgetOperation(operation.id, formData.user_name);
      } else {
        // Para otros tipos de acciones, usar el método original
        let finalFormData = { ...formData };
        
        // Si es un cambio de presupuesto, enriquecer la descripción automáticamente
        if (formData.action_type === 'budget_increased' && formData.new_budget) {
          const oldBudget = parseFloat(operation?.subir_ppto_diario_a || 0);
          const newBudget = parseFloat(formData.new_budget);
          const difference = newBudget - oldBudget;
          const changeType = difference > 0 ? 'Aumento' : difference < 0 ? 'Reducción' : 'Sin cambio';
          
          // Crear descripción enriquecida con información del cambio de presupuesto
          const enrichedDescription = `💬 COMENTARIO DEL USUARIO:
"${formData.description}"

═══════════════════════════════════════════════════════════════════════════════

📊 DETALLES DEL CAMBIO DE PRESUPUESTO:
• Presupuesto anterior: $${oldBudget.toFixed(2)}
• Presupuesto nuevo: $${newBudget.toFixed(2)}
• Tipo de cambio: ${changeType}
• Diferencia: $${Math.abs(difference).toFixed(2)}
• Porcentaje de cambio: ${oldBudget > 0 ? ((difference / oldBudget) * 100).toFixed(1) : '0'}%

⏰ Fecha y hora: ${new Date().toLocaleString('es-MX')}
👤 Usuario: ${formData.user_name}

═══════════════════════════════════════════════════════════════════════════════`;

          finalFormData.description = enrichedDescription;
        }
        
        await addBudgetOperationHistoryEntry(operation.id, finalFormData);
        
        // Show success message
        setShowSuccessMessage(true);
        
        // Wait a bit to show the success message, then close
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
        return;
      }

      // Agregar entrada al historial para las acciones de completado
      await addBudgetOperationHistoryEntry(operation.id, {
        action_type: formData.action_type,
        description: formData.description,
        user_name: formData.user_name
      });
      
      // Show success message
      setShowSuccessMessage(true);
      
      // Wait a bit to show the success message, then close
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template) => {
    setFormData(prev => ({
      ...prev,
      description: template
    }));
  };

  const selectedActionType = actionTypes.find(type => type.value === formData.action_type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">⚡ Acción Rápida</h2>
              <p className="text-green-100 mt-1">
                {operation.nombre} - {operation.campana}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo de Acción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Tipo de Acción
              </label>
              <div className="grid grid-cols-1 gap-3">
                {actionTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                      formData.action_type === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="action_type"
                      value={type.value}
                      checked={formData.action_type === type.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, action_type: e.target.value }))}
                      className="sr-only"
                    />
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${type.color} mr-3`}>
                      {type.label}
                    </span>
                    <div className="flex-1" />
                    {formData.action_type === type.value && (
                      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Templates Rápidos */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Templates Rápidos
              </label>
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                {quickTemplates[formData.action_type]?.map((template, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleTemplateClick(template)}
                    className="text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 text-sm border border-gray-200 hover:border-gray-300"
                  >
                    {template}
                  </button>
                ))}
              </div>
            </div>

            {/* Campo de Presupuesto (solo para budget_increased) */}
            {formData.action_type === 'budget_increased' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  💰 Nuevo Presupuesto Diario *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-lg">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.new_budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, new_budget: e.target.value }))}
                    className="w-full pl-8 pr-3 py-3 border-2 border-orange-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 text-lg font-semibold"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    Presupuesto actual: <span className="font-semibold">${parseFloat(operation?.subir_ppto_diario_a || 0).toFixed(2)}</span>
                  </span>
                  {formData.new_budget && (
                    <span className={`font-semibold ${
                      parseFloat(formData.new_budget) > parseFloat(operation?.subir_ppto_diario_a || 0) 
                        ? 'text-green-600' 
                        : parseFloat(formData.new_budget) < parseFloat(operation?.subir_ppto_diario_a || 0)
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}>
                      {parseFloat(formData.new_budget) > parseFloat(operation?.subir_ppto_diario_a || 0) 
                        ? '↗️ Aumento' 
                        : parseFloat(formData.new_budget) < parseFloat(operation?.subir_ppto_diario_a || 0)
                        ? '↘️ Reducción'
                        : '➡️ Sin cambio'
                      }: ${Math.abs(parseFloat(formData.new_budget) - parseFloat(operation?.subir_ppto_diario_a || 0)).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Descripción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descripción *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={
                  formData.action_type === 'budget_increased' 
                    ? "Explica el motivo del cambio de presupuesto..."
                    : "Describe la acción realizada..."
                }
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                rows="4"
                required
              />
            </div>

            {/* Success Message */}
            {showSuccessMessage && (
              <div className="bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded-xl flex items-center animate-pulse">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">✅ Acción agregada exitosamente al historial</span>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-6 rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </div>
                ) : (
                  'Agregar al Historial'
                )}
              </button>
              
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickActionModal;
